import React, { createContext, useContext, useEffect, useReducer, useRef, useState, useCallback } from "react";
import { getDataset, getFixtureById, getLiveFixtures } from "@/data";

const KEY = "btc_demo_v3";

const initialState = {
  profileId: "mid",
  language: "en",
  countdownSpeed: "real", // real | 60x | freeze
  scoringMode: "A", // A | B
  resultVariant: "A", // A | B
  fixtureStates: {}, // { [fixtureId]: STATE } override
  drafts: {}, // { [fixtureId]: { assignment, formation, bench } }
  submissions: {}, // { [fixtureId]: { assignment, formation, bench, submittedAt } }
  onboardingSeen: false,
  activeLeagueId: "elite",
  activeFixtureId: null,
  clockMs: 0, // virtual elapsed ms since epoch
};

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...initialState, ...JSON.parse(raw) };
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("[BeTheCoach] Failed to read demo state from localStorage:", e);
  }
  return { ...initialState };
}

function reducer(state, action) {
  switch (action.type) {
    case "SET": return { ...state, [action.key]: action.value };
    case "SET_FIXTURE_STATE":
      return { ...state, fixtureStates: { ...state.fixtureStates, [action.id]: action.state } };
    case "SAVE_DRAFT":
      return { ...state, drafts: { ...state.drafts, [action.id]: action.draft } };
    case "SUBMIT":
      return { ...state, submissions: { ...state.submissions, [action.id]: action.entry } };
    case "TICK":
      return { ...state, clockMs: state.clockMs + action.delta };
    case "RESET":
      return { ...initialState };
    default: return state;
  }
}

const DemoContext = createContext(null);

export function DemoProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, load);
  const [now, setNow] = useState(0);
  const clockRef = useRef(state.clockMs);
  clockRef.current = state.clockMs;

  // persist
  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[BeTheCoach] Failed to persist demo state to localStorage:", e);
    }
  }, [state]);

  // virtual clock ticker
  useEffect(() => {
    const mult = state.countdownSpeed === "60x" ? 60 : state.countdownSpeed === "freeze" ? 0 : 1;
    if (mult === 0) return undefined;
    let last = Date.now();
    const iv = setInterval(() => {
      const t = Date.now();
      const delta = (t - last) * mult;
      last = t;
      dispatch({ type: "TICK", delta });
      setNow((n) => n + 1);
    }, 300);
    return () => clearInterval(iv);
  }, [state.countdownSpeed]);

  // auto-lock OPEN fixtures whose countdown expired
  useEffect(() => {
    const ds = getDataset();
    ds.leagues.forEach((lg) => {
      const md = lg.matchdays.find((m) => m.isLive);
      md.fixtures.forEach((f) => {
        const cur = state.fixtureStates[f.id] || f.defaultState;
        if (cur === "OPEN" && f.lockOffsetMs !== undefined) {
          const remaining = f.lockOffsetMs - state.clockMs;
          if (remaining <= 0) dispatch({ type: "SET_FIXTURE_STATE", id: f.id, state: "LOCKED" });
        }
      });
    });
  }, [state.clockMs, state.fixtureStates]);

  const set = useCallback((key, value) => dispatch({ type: "SET", key, value }), [dispatch]);
  const setFixtureState = useCallback((id, s) => dispatch({ type: "SET_FIXTURE_STATE", id, state: s }), [dispatch]);
  const saveDraft = useCallback((id, draft) => dispatch({ type: "SAVE_DRAFT", id, draft }), [dispatch]);
  const submit = useCallback((id, entry) => dispatch({ type: "SUBMIT", id, entry }), [dispatch]);
  const reset = useCallback(() => { dispatch({ type: "RESET" }); }, [dispatch]);

  const getState = useCallback((fixture) => {
    if (!fixture) return "OPEN";
    return state.fixtureStates[fixture.id] || fixture.defaultState || "OPEN";
  }, [state.fixtureStates]);

  const remainingMs = useCallback((fixture) => {
    if (!fixture || fixture.lockOffsetMs === undefined) return null;
    return fixture.lockOffsetMs - state.clockMs;
  }, [state.clockMs]);

  const dataset = getDataset();
  const profile = dataset.profiles[state.profileId];
  const activeFixtureId = state.activeFixtureId || getLiveFixtures("elite")[0].id;

  const value = {
    state, dispatch, set, setFixtureState, saveDraft, submit, reset,
    getState, remainingMs, dataset, profile, now,
    getFixtureById, activeFixtureId,
  };
  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be inside DemoProvider");
  return ctx;
}
